import { dayjs } from '@trip-ux/common';
import { DAYJS_DIFF } from '@trip-ux/constants';
import { get, isEmpty } from 'lodash';
import { CalendarDate, parseDate } from '@internationalized/date';

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MILLISECONDS_PER_DAY = MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR * HOURS_PER_DAY;
const DATE_COMPONENT_PADDING = 2;
const PADDING_CHAR = '0';
const REPORT_MIN_YEAR = 2024;
export const REPORT_MIN_DATE = new CalendarDate(REPORT_MIN_YEAR, 1, 1);

export interface ValidationDates {
  educationStartDate?: string;
  educationEndDate?: string;
  tripStartDate?: string;
  leavingDate?: string;
  daysCoverageBeforeSchool?: number;
  daysCoverageAfterSchool?: number;
}

export interface ValidationDatesForTopUp {
  startDate: string;
  endDate: string;
  tripBookingDate: string;
}

const checkEmptyValue = (checkValue, returnValue) => {
  if (!isEmpty(checkValue)) {
    return returnValue;
  }
  return null;
};

export const isCalendarDateLike = (value: unknown): value is { year: number; month: number; day: number } =>
  typeof value === 'object' && value !== null && 'year' in value && 'month' in value && 'day' in value;

export const toCalendarDate = (value: unknown): CalendarDate | undefined => {
  if (!value) {
    return undefined;
  }
  if (value instanceof CalendarDate) {
    return value;
  }
  if (isCalendarDateLike(value)) {
    return new CalendarDate(value.year, value.month, value.day);
  }
  if (typeof value === 'string') {
    try {
      return parseDate(value);
    } catch {
      return undefined;
    }
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return undefined;
    }
    return new CalendarDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }
  return undefined;
};

/** Calculate days between two CalendarDates (inclusive) */
export const daysBetween = (start: CalendarDate, end: CalendarDate): number => {
  const startDate = Date.UTC(start.year, start.month - 1, start.day);
  const endDate = Date.UTC(end.year, end.month - 1, end.day);
  const diffTime = Math.abs(endDate - startDate);
  return Math.floor(diffTime / MILLISECONDS_PER_DAY) + 1;
};

/** Compare CalendarDates: returns negative if a < b, 0 if equal, positive if a > b */
export const compareCalendarDates = (a: CalendarDate, b: CalendarDate): number => {
  if (a.year !== b.year) {
    return a.year - b.year;
  }
  if (a.month !== b.month) {
    return a.month - b.month;
  }
  return a.day - b.day;
};

/** Format CalendarDate-like value to YYYY-MM-DD string */
export const formatCalendarDate = (date: unknown): string => {
  if (!date) { return ''; }
  if (typeof date === 'string') { return date; }
  if (isCalendarDateLike(date)) {
    return `${date.year}-${String(date.month).padStart(DATE_COMPONENT_PADDING, PADDING_CHAR)}-${String(date.day).padStart(DATE_COMPONENT_PADDING, PADDING_CHAR)}`;
  }
  return '';
};

export const getSchoolDisabledDays = (
  startDate: string,
  endDate: string,
  tripBookingDate: string,
  validationDates: ValidationDates,
  dateFormat: string
): { startDate?: { before: Date, after: Date }; endDate?: { before: Date, after: Date } } => {
  const coverageStartDate = dayjs(startDate, dateFormat, true);
  const coverageStartDateIsValid = coverageStartDate.isValid();

  const coverageEndDate = dayjs(endDate, dateFormat, true);
  const coverageEndDateIsValid = coverageEndDate.isValid();

  const educationEndDateValue = get(validationDates, 'educationEndDate', '');
  const educationEndDate = dayjs(educationEndDateValue, dateFormat);
  const daysCoverageAfterSchool = get(validationDates, 'daysCoverageAfterSchool', 0);
  let studentEndDateLimit = null;
  if (!isEmpty(educationEndDateValue) && educationEndDate.isValid() && typeof daysCoverageAfterSchool === 'number' && !isNaN(daysCoverageAfterSchool)) {
    studentEndDateLimit = educationEndDate.add(daysCoverageAfterSchool, DAYJS_DIFF.DAYS);
  }

  const studentEndDateAfterDisabledDay = studentEndDateLimit?.toDate();

  return {
    startDate: {
      before: dayjs(tripBookingDate, dateFormat).toDate(),
      after: coverageEndDateIsValid ? coverageEndDate.toDate() : studentEndDateAfterDisabledDay//Should this be earliest Purchase Date
    },
    endDate: {
      before: coverageStartDateIsValid ? coverageStartDate.toDate() : new Date(),
      after: studentEndDateAfterDisabledDay
    }
  };
};

export const getTopUpDisabledDays = (
  validationDates: ValidationDatesForTopUp,
  tripBookingDate: string,
  planCoverageDayCount: number,
  maxTripDuration: number,
  dateFormat: string
): { startDate?: { before?: Date; after: Date }; endDate?: { before: Date; after: Date } } => {
  const { startDate, endDate } = validationDates;

  let maxStartDate = null;
  const baseDate = dayjs(tripBookingDate, dateFormat);
  if (baseDate.isValid()) {
    maxStartDate = baseDate.add(180, DAYJS_DIFF.DAYS).toDate();
  }

  return {
    startDate: {
      before: dayjs(tripBookingDate, dateFormat).toDate(),
      after: maxStartDate
    },
    endDate: {
      before: dayjs(startDate, dateFormat).add(planCoverageDayCount, DAYJS_DIFF.DAYS).toDate(),
      after: dayjs(startDate, dateFormat).add(maxTripDuration, DAYJS_DIFF.DAYS).toDate()
    }
  };
};

export const getMultiTripDisabledDays = (
  maxTripDuration: number,
  planCode: string,
  tripBookingDate: string,
  startDate: string,
  dateFormat: string
): { startDate: { before: Date; after: Date }; endDate: { before: Date; after: Date } } => {
  const earliestPurchaseDate = planCode === 'MAIH' ? 1095 : 180 // Multiple all inclusive plans can be purchased at most 1095 days in advance
  let maxStartDate = null;
  const baseDate = dayjs(tripBookingDate, dateFormat);
  if (baseDate.isValid() && typeof earliestPurchaseDate === 'number' && !isNaN(earliestPurchaseDate)) {
    maxStartDate = baseDate.add(earliestPurchaseDate, DAYJS_DIFF.DAYS).subtract(1, DAYJS_DIFF.DAYS).toDate();
  }
  return {
    startDate: {
      before: dayjs(tripBookingDate, dateFormat).toDate(),
      after: maxStartDate
    },
    endDate: {
      before: dayjs(startDate, dateFormat).toDate(),
      after: dayjs(startDate).add(maxTripDuration, DAYJS_DIFF.DAYS).subtract(1, DAYJS_DIFF.DAYS).toDate()
    }
  };
};
